import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { canWrite } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SupplierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [supplier, setSupplier] = useState(null);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const [yearFilter, setYearFilter] = useState('all');
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    notes: '',
    payment_method: 'cash'
  });
  const [invoiceData, setInvoiceData] = useState({
    date: new Date().toISOString().split('T')[0],
    items: [{ code: '', description: '', qty_unit: 1, unit_price: 0 }],
    vat_percentage: 0,
    discount: 0,
    notes: ''
  });

  const fetchSupplierDetails = async () => {
    try {
      const response = await axios.get(`${API}/suppliers/by-id/${id}`, { headers: getAuthHeader() });
      setSupplier(response.data.supplier);
      setPayments(response.data.payments);
      const invRes = await axios.get(`${API}/supplier-invoices?supplier_name=${encodeURIComponent(response.data.supplier.name)}`, { headers: getAuthHeader() });
      setInvoices(invRes.data);
    } catch (error) {
      toast.error('Failed to fetch supplier details');
      navigate('/finance');
    }
  };

  useEffect(() => {
    fetchSupplierDetails();
  }, [id]);

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payments`, {
        entity_id: supplier.name,
        entity_type: 'supplier',
        ...paymentData
      }, { headers: getAuthHeader() });
      toast.success('Payment recorded');
      setIsPaymentOpen(false);
      fetchSupplierDetails();
      setPaymentData({ amount: 0, date: new Date().toISOString().split('T')[0], notes: '', payment_method: 'cash' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const addInvoiceItem = () => setInvoiceData({ ...invoiceData, items: [...invoiceData.items, { code: '', description: '', qty_unit: 1, unit_price: 0 }] });
  const removeInvoiceItem = (idx) => setInvoiceData({ ...invoiceData, items: invoiceData.items.filter((_, i) => i !== idx) });
  const updateInvoiceItem = (idx, field, value) => {
    const items = [...invoiceData.items];
    items[idx] = { ...items[idx], [field]: value };
    setInvoiceData({ ...invoiceData, items });
  };

  const invoiceSubtotal = invoiceData.items.reduce((sum, item) => sum + (parseFloat(item.qty_unit) || 0) * (parseFloat(item.unit_price) || 0), 0);
  const discountedBase = invoiceSubtotal - (parseFloat(invoiceData.discount) || 0);
  const invoiceVat = discountedBase * (invoiceData.vat_percentage / 100);
  const invoiceTotal = discountedBase + invoiceVat;

  const handleAddInvoice = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/supplier-invoices`, {
        supplier_id: supplier.id,
        supplier_name: supplier.name,
        items: invoiceData.items,
        subtotal: invoiceSubtotal,
        vat_percentage: invoiceData.vat_percentage,
        vat_amount: invoiceVat,
        discount: parseFloat(invoiceData.discount) || 0,
        total: invoiceTotal,
        date: invoiceData.date,
        notes: invoiceData.notes || null
      }, { headers: getAuthHeader() });
      toast.success('Invoice added');
      setIsInvoiceOpen(false);
      fetchSupplierDetails();
      setInvoiceData({ date: new Date().toISOString().split('T')[0], items: [{ code: '', description: '', qty_unit: 1, unit_price: 0 }], vat_percentage: 0, discount: 0, notes: '' });
    } catch (error) {
      toast.error('Failed to add invoice');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await axios.delete(`${API}/supplier-invoices/${invoiceId}`, { headers: getAuthHeader() });
      toast.success('Invoice deleted');
      fetchSupplierDetails();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  if (!supplier) return <div className="p-8">Loading...</div>;

  const availableYears = [...new Set([
    ...invoices.map(i => (i.date || '').slice(0, 4)),
    ...payments.map(p => (p.date || '').slice(0, 4)),
  ].filter(Boolean))].sort().reverse();

  const filteredInvoices = invoices.filter(i =>
    yearFilter === 'all' || (i.date || '').slice(0, 4) === yearFilter
  );
  const filteredPayments = payments.filter(p =>
    yearFilter === 'all' || (p.date || '').slice(0, 4) === yearFilter
  );

  const totalInvoiced = invoices.reduce((sum, inv) => sum + inv.total, 0);
  const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
  const balanceOwed = totalInvoiced - totalPayments;

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/finance')} data-testid="back-to-finance-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-4xl font-bold tracking-tight" lang={language === 'ar' ? 'ar' : 'en'} data-testid="supplier-detail-title">
          {supplier.name}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border border-border">
          <h3 className="text-lg font-semibold mb-4" lang={language === 'ar' ? 'ar' : 'en'}>Contact Information</h3>
          <div className="space-y-2 text-sm">
            {supplier.registration_number && <div><span className="text-muted-foreground">Reg. No:</span><p className="font-medium">{supplier.registration_number}</p></div>}
            <div><span className="text-muted-foreground">{t('email')}:</span><p className="font-medium">{supplier.email || 'N/A'}</p></div>
            <div><span className="text-muted-foreground">Phone:</span><p className="font-medium">{supplier.phone || 'N/A'}</p></div>
            <div><span className="text-muted-foreground">Address:</span><p className="font-medium">{supplier.address || 'N/A'}</p></div>
          </div>
        </Card>

        <Card className="p-6 border border-border">
          <h3 className="text-lg font-semibold mb-4">Financial Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Invoiced:</span>
              <span className="font-mono-numeric font-semibold text-destructive">{totalInvoiced.toFixed(2)} {t('currency')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Paid:</span>
              <span className="font-mono-numeric font-semibold text-success">{totalPayments.toFixed(2)} {t('currency')}</span>
            </div>
            <div className="flex justify-between border-t border-border pt-2 mt-2">
              <span className="font-semibold">Balance Owed:</span>
              <span className={`font-mono-numeric font-bold ${balanceOwed > 0 ? 'text-warning' : 'text-success'}`}>{balanceOwed.toFixed(2)} {t('currency')}</span>
            </div>
          </div>
        </Card>
      </div>

      <Tabs defaultValue="invoices" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="payments">{t('payments')}</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices">
          <Card className="p-6 border border-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">Purchase Invoices</h2>
              {canWrite(user?.role) && <Dialog open={isInvoiceOpen} onOpenChange={setIsInvoiceOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-accent hover:bg-accent/90">
                    <Plus className="w-4 h-4" />
                    Add Invoice
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add Purchase Invoice</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddInvoice} className="space-y-4">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={invoiceData.date} onChange={(e) => setInvoiceData({ ...invoiceData, date: e.target.value })} required />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <Label>Items</Label>
                        <button type="button" onClick={addInvoiceItem} className="text-xs text-accent hover:underline">+ Add row</button>
                      </div>
                      <div className="space-y-1">
                        <div className="grid grid-cols-[80px_1fr_90px_90px_24px] gap-1 text-xs text-muted-foreground px-1">
                          <span>Code</span><span>Description</span><span>Qty/Unit</span><span className="text-end">Unit Price</span><span />
                        </div>
                        {invoiceData.items.map((item, idx) => (
                          <div key={idx} className="grid grid-cols-[80px_1fr_90px_90px_24px] gap-1 items-center">
                            <Input placeholder="Code" value={item.code} onChange={(e) => updateInvoiceItem(idx, 'code', e.target.value)} className="h-8 text-xs" />
                            <Input placeholder="Description" value={item.description} onChange={(e) => updateInvoiceItem(idx, 'description', e.target.value)} className="h-8 text-xs" />
                            <Input type="number" min="0" step="any" placeholder="1" value={item.qty_unit} onChange={(e) => updateInvoiceItem(idx, 'qty_unit', e.target.value)} className="h-8 text-xs" />
                            <Input type="number" step="0.01" placeholder="0.00" value={item.unit_price} onChange={(e) => updateInvoiceItem(idx, 'unit_price', e.target.value)} className="h-8 text-xs text-end" />
                            {invoiceData.items.length > 1 ? (
                              <button type="button" onClick={() => removeInvoiceItem(idx)} className="text-destructive flex items-center justify-center">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            ) : <span />}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>VAT %</Label>
                        <div className="flex gap-2 mt-1">
                          {[0, 14].map(v => (
                            <button key={v} type="button" onClick={() => setInvoiceData({ ...invoiceData, vat_percentage: v })}
                              className={`flex-1 py-1.5 rounded-md border text-sm font-medium transition-colors ${invoiceData.vat_percentage === v ? 'bg-accent text-accent-foreground border-accent' : 'border-input bg-background'}`}>
                              {v}%
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label>Discount</Label>
                        <Input type="number" step="0.01" value={invoiceData.discount} onChange={(e) => setInvoiceData({ ...invoiceData, discount: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Input value={invoiceData.notes} onChange={(e) => setInvoiceData({ ...invoiceData, notes: e.target.value })} />
                    </div>
                    <div className="bg-muted/30 p-3 rounded-md text-sm space-y-1">
                      <div className="flex justify-between"><span className="text-muted-foreground">Subtotal:</span><span className="font-mono-numeric">{invoiceSubtotal.toFixed(2)}</span></div>
                      {parseFloat(invoiceData.discount) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discount:</span><span className="font-mono-numeric">-{parseFloat(invoiceData.discount).toFixed(2)}</span></div>}
                      {invoiceData.vat_percentage > 0 && <div className="flex justify-between"><span className="text-muted-foreground">VAT ({invoiceData.vat_percentage}% on {discountedBase.toFixed(2)}):</span><span className="font-mono-numeric">{invoiceVat.toFixed(2)}</span></div>}
                      <div className="flex justify-between font-semibold border-t border-border pt-1"><span>Total:</span><span className="font-mono-numeric">{invoiceTotal.toFixed(2)} {t('currency')}</span></div>
                    </div>
                    <Button type="submit" className="w-full bg-accent hover:bg-accent/90">{t('save')}</Button>
                  </form>
                </DialogContent>
              </Dialog>}
            </div>
            <div className="flex justify-end mb-3">
              <select
                value={yearFilter}
                onChange={e => setYearFilter(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="all">All Years</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="border border-border p-3 text-start font-semibold">Invoice #</th>
                    <th className="border border-border p-3 text-start font-semibold">Date</th>
                    <th className="border border-border p-3 text-start font-semibold">Items</th>
                    <th className="border border-border p-3 text-end font-semibold">VAT</th>
                    <th className="border border-border p-3 text-end font-semibold">Total</th>
                    {user?.role === 'admin' && <th className="border border-border p-3 text-center font-semibold">{t('actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((inv, idx) => (
                    <tr key={idx} className="hover:bg-muted/30">
                      <td className="border border-border p-3 font-mono text-xs">{inv.invoice_number}</td>
                      <td className="border border-border p-3">{inv.date}</td>
                      <td className="border border-border p-3 text-muted-foreground text-xs">{inv.items?.map(i => i.description).filter(Boolean).join(', ') || '-'}</td>
                      <td className="border border-border p-3 text-end">{inv.vat_percentage > 0 ? `${inv.vat_percentage}%` : '-'}</td>
                      <td className="border border-border p-3 text-end font-mono-numeric font-semibold">{inv.total.toFixed(2)} {t('currency')}</td>
                      {user?.role === 'admin' && (
                        <td className="border border-border p-3 text-center">
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteInvoice(inv.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredInvoices.length === 0 && <div className="text-center py-8 text-muted-foreground">No invoices yet</div>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card className="p-6 border border-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">{t('payments')}</h2>
              {canWrite(user?.role) && <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-accent hover:bg-accent/90" data-testid="add-payment-btn">
                    <Plus className="w-4 h-4" />
                    Record Payment
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Record Payment</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPayment} className="space-y-4">
                    <div>
                      <Label>Amount ({t('currency')})</Label>
                      <Input type="number" step="0.01" value={paymentData.amount} onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) })} required data-testid="payment-amount-input" />
                    </div>
                    <div>
                      <Label>{t('date')}</Label>
                      <Input type="date" value={paymentData.date} onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })} required data-testid="payment-date-input" />
                    </div>
                    <div>
                      <Label>Payment Method</Label>
                      <div className="flex gap-2 mt-1">
                        <button type="button" onClick={() => setPaymentData({ ...paymentData, payment_method: 'cash' })}
                          className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${paymentData.payment_method === 'cash' ? 'bg-accent text-accent-foreground border-accent' : 'border-input bg-background'}`}>
                          Cash
                        </button>
                        <button type="button" onClick={() => setPaymentData({ ...paymentData, payment_method: 'bank' })}
                          className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${paymentData.payment_method === 'bank' ? 'bg-accent text-accent-foreground border-accent' : 'border-input bg-background'}`}>
                          Bank Transfer
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Input value={paymentData.notes} onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })} data-testid="payment-notes-input" />
                    </div>
                    <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="payment-submit-btn">{t('save')}</Button>
                  </form>
                </DialogContent>
              </Dialog>}
            </div>
            <div className="flex justify-end mb-3">
              <select
                value={yearFilter}
                onChange={e => setYearFilter(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="all">All Years</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="border border-border p-3 text-start font-semibold">{t('date')}</th>
                    <th className="border border-border p-3 text-end font-semibold">Amount</th>
                    <th className="border border-border p-3 text-start font-semibold">Method</th>
                    <th className="border border-border p-3 text-start font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment, idx) => (
                    <tr key={idx} className="hover:bg-muted/30" data-testid={`payment-row-${idx}`}>
                      <td className="border border-border p-3">{payment.date}</td>
                      <td className="border border-border p-3 text-end font-mono-numeric text-destructive">{payment.amount.toFixed(2)} {t('currency')}</td>
                      <td className="border border-border p-3 capitalize">{payment.payment_method || 'cash'}</td>
                      <td className="border border-border p-3">{payment.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredPayments.length === 0 && <div className="text-center py-8 text-muted-foreground">No payments recorded yet</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
